import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation, Outlet } from 'react-router-dom';
import { Layout, Avatar, Dropdown, Space, Typography, Grid } from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  CreditCardOutlined,
  BarChartOutlined,
  LogoutOutlined,
  SettingOutlined,
  NotificationOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { api, API_BASE } from '../lib/api';
import { AnnouncementHost } from '@annea/announcements-ui';
import { auth } from '../lib/auth';
import type { User } from '../types';
import StandardMenu from './StandardMenu';

const { Header, Content } = Layout;
const { Text } = Typography;
const { useBreakpoint } = Grid;
type MenuItem = NonNullable<MenuProps['items']>[number];

export default function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [canManageOrganization, setCanManageOrganization] = useState(false);
  const [isAnneaOrgAdmin, setIsAnneaOrgAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      console.log('[MainLayout] Loading user session...');
      const userData = await api.auth.getSession();
      const profileData = await api.user.me().catch(() => null);
      if (!userData || typeof userData !== 'object' || !('id' in userData)) {
        auth.clear();
        setUser(null);
        navigate('/login');
        return;
      }
      const mergedUser = {
        ...(userData || {}),
        ...(profileData || {}),
      } as User;
      console.log('[MainLayout] User session loaded:', userData);
      auth.setAuthenticated();
      setUser(mergedUser);
      try {
        const orgData = await api.organizations.list();
        const orgs = orgData.organizations || [];
        const canManage = orgs.some(
          (org: { role?: string }) => org.role && org.role !== 'MEMBER'
        );
        setCanManageOrganization(canManage || orgs.length === 0);
        const anneaSlug = import.meta.env.VITE_ANNEA_ORG_SLUG || 'annea';
        const inAnneaAdminRole = orgs.some(
          (org: { slug?: string; role?: string }) =>
            org.slug === anneaSlug && (org.role === 'OWNER' || org.role === 'ADMIN')
        );
        setIsAnneaOrgAdmin(inAnneaAdminRole);
      } catch {
        setCanManageOrganization(false);
        setIsAnneaOrgAdmin(false);
      }
    } catch (error: any) {
      console.error('[MainLayout] Failed to load user session:', error);
      auth.clear();
      navigate('/login');
    } finally {
      console.log('[MainLayout] Setting loading to false');
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.auth.signOut();
    } catch (error) {
      console.warn('Sign out failed:', error);
    } finally {
      auth.clear();
      navigate('/login');
    }
  };

  const userMenuItems: MenuItem[] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile Settings',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'announcements',
      icon: <NotificationOutlined />,
      label: 'News & Updates',
      onClick: () => navigate('/announcements'),
    },
    {
      key: 'subscriptions',
      icon: <CreditCardOutlined />,
      label: 'Subscriptions',
      onClick: () => navigate('/subscriptions'),
    },
    canManageOrganization
      ? {
          key: 'organizations',
          icon: <TeamOutlined />,
          label: 'Organization',
          onClick: () => navigate('/organizations'),
        }
      : null,
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
    },
  ].filter(Boolean) as MenuItem[];

  const adminMenuItems: MenuItem[] = [
    isAnneaOrgAdmin
      ? {
          key: 'analytics',
          icon: <BarChartOutlined />,
          label: 'Analytics',
          onClick: () => navigate('/analytics'),
        }
      : null,
    isAnneaOrgAdmin
      ? {
          key: 'analytics-events',
          icon: <BarChartOutlined />,
          label: 'Analytics Events',
          onClick: () => navigate('/analytics/events'),
        }
      : null,
    isAnneaOrgAdmin
      ? {
          key: 'analytics-users-orgs',
          icon: <BarChartOutlined />,
          label: 'Users & Organizations Analytics',
          onClick: () => navigate('/analytics/users-organizations'),
        }
      : null,
    isAnneaOrgAdmin
      ? {
          key: 'analytics-expired-access-report',
          icon: <BarChartOutlined />,
          label: 'Expired Subscriptions Report',
          onClick: () => navigate('/analytics/expired-access-report'),
        }
      : null,
    isAnneaOrgAdmin
      ? {
          key: 'analytics-aixm',
          icon: <BarChartOutlined />,
          label: 'AIXM Viewer Analytics',
          onClick: () => navigate('/analytics/aixm-viewer-light'),
        }
      : null,
    user?.isSuperAdmin
      ? {
          key: 'admin-apps',
          icon: <SettingOutlined />,
          label: 'Admin: Apps',
          onClick: () => navigate('/admin/apps'),
        }
      : null,
    user?.isSuperAdmin
      ? {
          key: 'admin-orgs',
          icon: <TeamOutlined />,
          label: 'Admin: Organizations',
          onClick: () => navigate('/admin/organizations'),
        }
      : null,
    user?.isSuperAdmin
      ? {
          key: 'admin-users',
          icon: <UserOutlined />,
          label: 'Admin: Users',
          onClick: () => navigate('/admin/users'),
        }
      : null,
    user?.isSuperAdmin
      ? {
          key: 'admin-terrain',
          icon: <BarChartOutlined />,
          label: 'Admin: Terrain',
          onClick: () => navigate('/admin/terrain'),
        }
      : null,
    user?.isSuperAdmin
      ? {
          key: 'admin-announcements',
          icon: <NotificationOutlined />,
          label: 'Admin: Announcements',
          onClick: () => navigate('/admin/announcements'),
        }
      : null,
    user?.isSuperAdmin
      ? {
          key: 'admin-export-import',
          icon: <SettingOutlined />,
          label: 'Export/Import',
          onClick: () => navigate('/admin/export-import'),
        }
      : null,
  ].filter(Boolean) as MenuItem[];

  const screens = useBreakpoint();
  const isMobile = !screens.sm;
  const headerPadding = isMobile ? '0 12px' : '0 24px';
  const outerPadding = isMobile ? 0 : 24;
  const contentPadding = isMobile ? 12 : 24;
  const isAdminVisible = adminMenuItems.length > 0;

  if (loading) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <div style={{ padding: 24, textAlign: 'center' }}>Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#f46524',
          padding: headerPadding,
          position: 'sticky',
          // Below the environment bar, when a non-production APO shows one.
          top: 'var(--annea-environment-bar-height, 0px)',
          zIndex: 1000,
          height: 'auto',
          minHeight: 64,
          lineHeight: 'normal',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, overflow: 'hidden' }}>
          <Link
            to="/apps"
            style={{ color: 'white', fontSize: isMobile ? 16 : 20, fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            {isMobile ? 'Annea Apps' : 'Annea Apps Portal'}
          </Link>
          {!isMobile && location.pathname !== '/apps' && (
            <Link to="/apps" style={{ color: 'white', opacity: 0.9 }}>
              App Catalog
            </Link>
          )}
        </div>

        <Space size="small" align="center">
          {isAdminVisible && (
            <Dropdown menu={{ items: adminMenuItems }} placement="bottomRight">
              <Text style={{ color: 'white', cursor: 'pointer' }}>Admin Menu</Text>
            </Dropdown>
          )}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <StandardMenu />
          </div>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Space style={{ cursor: 'pointer' }}>
              <Avatar size={36} icon={<UserOutlined />} />
              {/*<Text style={{ color: 'white' }}>
                {user?.name || user?.email}
              </Text>*/}
            </Space>
          </Dropdown>
        </Space>
      </Header>

      <Layout style={{ padding: outerPadding, background: '#f7f8fa' }}>
        <Content
          style={{
            background: '#fff',
            padding: contentPadding,
            margin: 0,
            minHeight: 280,
            borderRadius: isMobile ? 0 : 8,
          }}
        >
          <Outlet />
        </Content>
      </Layout>
      <AnnouncementHost apiUrl={API_BASE} channel="apo" />
    </Layout>
  );
}
